import { z } from 'zod';
import { MAX_EMERGENCY_CONTACTS, candidateCredentialSchema, educationEntrySchema, emergencyContactSchema, employmentHistoryEntrySchema, employmentReferenceSchema, personAddressSchema, } from './details.js';
/**
 * The profile fields a portal form may feed (ADR-013 §1).
 *
 * A CLOSED vocabulary. A step type's `profileFields` names only these, and the projection
 * validates every value against the schema here, so a form cannot invent a field or hand the
 * profile a shape nothing renders. Keys that are also Person paths use the Person path, which
 * is what lets an HR edit (`person.updated`, whose `changedFields` are dotted Person paths) be
 * compared field by field with an approved answer.
 *
 * Nothing here is a person attribute. Attributes are trigger answers (ADR-006), and an
 * unreviewed answer must never trigger anything (ADR-013 §2), so a candidate's words cannot
 * reach one through this list. SSN and date of birth are absent too: they never enter a draft
 * or submission (ADR-012 §2).
 */
export const profileFieldSchema = z.enum([
    'name.givenName',
    'name.middleName',
    'name.familyName',
    'identity.previousName',
    'contact.email',
    'contact.phone',
    'contact.alternatePhone',
    'contact.preferredCallTime',
    'contact.address',
    'contact.emergencyContacts',
    'employmentHistory',
    'references',
    'education',
    'credentials',
]);
const text = z.string().trim().min(1).max(500);
const phone = z.string().trim().min(7).max(50);
/** What each field's value must look like. Parsed on the way out of every step type. */
export const profileFieldValueSchemas = {
    'name.givenName': text,
    'name.middleName': text,
    'name.familyName': text,
    'identity.previousName': text,
    'contact.email': z.string().trim().email(),
    'contact.phone': phone,
    'contact.alternatePhone': phone,
    'contact.preferredCallTime': z.string().trim().min(1).max(200),
    'contact.address': personAddressSchema,
    'contact.emergencyContacts': z.array(emergencyContactSchema).min(1).max(MAX_EMERGENCY_CONTACTS),
    employmentHistory: z.array(employmentHistoryEntrySchema).min(1).max(10),
    references: z.array(employmentReferenceSchema).min(1).max(5),
    education: z.array(educationEntrySchema).min(1).max(10),
    credentials: z.array(candidateCredentialSchema).min(1).max(10),
};
/**
 * Fields that live on the Person record, where HR maintains a current value. The rest
 * (history, references, education, credentials) exist only as candidate answers.
 */
const ON_PERSON = new Set([
    'name.givenName', 'name.middleName', 'name.familyName', 'identity.previousName',
    'contact.email', 'contact.phone', 'contact.alternatePhone', 'contact.preferredCallTime',
    'contact.address', 'contact.emergencyContacts',
]);
export function isPersonProfileField(field) {
    return ON_PERSON.has(field);
}
/**
 * Where portal links and messages go (ADR-013 §2, Q1). A candidate's answer for one of these
 * is NEVER effective, even once its form is approved: whoever controls the address controls
 * every future link, and a link holder must not be able to redirect it. It stays pending until
 * HR chooses "Use the candidate's answer", an ordinary `people.update`.
 */
const DELIVERY = new Set(['contact.email', 'contact.phone']);
export function isDeliveryProfileField(field) {
    return DELIVERY.has(field);
}
/** One value a step type reads out of a draft or submission. */
export const profileFieldValueSchema = z.object({ field: profileFieldSchema, value: z.unknown() }).strict();
/** Nothing typed: absent, blank text, or containers holding only blanks. */
export function isBlankProfileValue(value) {
    if (value === undefined || value === null)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.every(isBlankProfileValue);
    if (typeof value === 'object')
        return Object.values(value).every(isBlankProfileValue);
    return false;
}
/**
 * Validate what a step type emitted: known field, value of the right shape, not blank. Anything
 * else is dropped rather than shown, because a malformed value on a profile is worse than none.
 */
export function cleanProfileFieldValues(values) {
    const seen = new Set();
    return values.flatMap(({ field, value }) => {
        if (seen.has(field) || isBlankProfileValue(value))
            return [];
        const parsed = profileFieldValueSchemas[field].safeParse(value);
        if (!parsed.success)
            return [];
        seen.add(field);
        return [{ field, value: parsed.data }];
    });
}
//# sourceMappingURL=profile-fields.js.map